/**
 * Resources Service Hooks
 * React hooks for accessing resource management services
 */
import { useCCMS } from '../useCCMS';
/**
 * Hook to access all resources-related services
 * @returns ResourcesServices object containing all resources service instances
 */
export function useResourcesServices() {
    const { services } = useCCMS();
    if (!services?.resources) {
        throw new Error('Resources services not available. Ensure CCMSProvider is configured with resources services.');
    }
    return services.resources;
}
/**
 * Hook to access CourseService
 * @returns CourseService instance
 */
export function useCourseService() {
    return useResourcesServices().course;
}
/**
 * Hook to access CourseCategoryService
 * @returns CourseCategoryService instance
 */
export function useCourseCategoryService() {
    return useResourcesServices().courseCategory;
}
/**
 * Hook to access CourseSessionService
 * @returns CourseSessionService instance
 */
export function useCourseSessionService() {
    return useResourcesServices().courseSession;
}
/**
 * Hook to access UsageLogService
 * @returns UsageLogService instance
 */
export function useUsageLogService() {
    return useResourcesServices().usageLog;
}
/**
 * Hook to access CourseStatusService
 * @returns CourseStatusService instance
 */
export function useCourseStatusService() {
    return useResourcesServices().courseStatus;
}
/**
 * Hook to access CourseCategoryStatusService
 * @returns CourseCategoryStatusService instance
 */
export function useCourseCategoryStatusService() {
    return useResourcesServices().courseCategoryStatus;
}
/**
 * Hook to access CourseSessionStatusService
 * @returns CourseSessionStatusService instance
 */
export function useCourseSessionStatusService() {
    return useResourcesServices().courseSessionStatus;
}
/**
 * Hook to access CourseSessionTypeService
 * @returns CourseSessionTypeService instance
 */
export function useCourseSessionTypeService() {
    return useResourcesServices().courseSessionType;
}
/**
 * Hook to access PublicCoursesService (anonymous-friendly catalog reads)
 */
export function usePublicCoursesService() {
    return useResourcesServices().publicCourses;
}
/**
 * Hook to access PublicCategoriesService (legacy non-enriched)
 */
export function usePublicCategoriesService() {
    return useResourcesServices().publicCategories;
}
/**
 * Hook to access PublicCategoriesEnrichedService (categories with course counts + ministry/visibility joins)
 */
export function usePublicCategoriesEnrichedService() {
    return useResourcesServices().publicCategoriesEnriched;
}
/**
 * Hook to access UserCourseProgressService
 */
export function useUserCourseProgressService() {
    return useResourcesServices().userCourseProgress;
}
/**
 * Hook to access UserSessionProgressService
 */
export function useUserSessionProgressService() {
    return useResourcesServices().userSessionProgress;
}
/**
 * Hook to access UserProgressStatusService
 */
export function useUserProgressStatusService() {
    return useResourcesServices().userProgressStatus;
}
/**
 * Hook to access CourseVisibilityService
 */
export function useCourseVisibilityService() {
    return useResourcesServices().courseVisibility;
}
/**
 * Hook to access CoursePrerequisiteService
 */
export function useCoursePrerequisiteService() {
    return useResourcesServices().coursePrerequisite;
}
/**
 * Hook to access ResourceUsageActionService
 */
export function useResourceUsageActionService() {
    return useResourcesServices().resourceUsageAction;
}
/**
 * Hook to access ResourceSettingsService (Resource Centre platform settings)
 */
export function useResourceSettingsService() {
    return useResourcesServices().resourceSettings;
}
/**
 * Hook to access ResourceMediaService (streaming media backfill, admin)
 */
export function useResourceMediaService() {
    return useResourcesServices().resourceMedia;
}
//# sourceMappingURL=useResourcesServices.js.map